import { DeferredCodeBlock as CodeBlock } from "@prismicio/editor-fields"
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardImage,
  Dialog,
  DialogContent,
  DialogHeader,
  ScrollArea,
  Text
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"

import {
  emptyMigrationReleasesUrl,
  migrationApiDocsUrl,
  migrationApiTryOutUrl,
  permanentTokenDocsUrl,
  userSessionTokenDocsUrl
} from "../../app/constants"
import { staticEditorConfig } from "../../app/editorConfig"
import { migrationReleaseNoModal } from "../../app/integrations/AmplitudeExperiments"

export function MigrationReleaseEmptyBody() {
  const [modalOpen, setModalOpen] = useState(false)

  if (migrationReleaseNoModal() === "off") {
    return (
      <Card
        padding={0}
        sx={{ width: 498, marginTop: 80, marginInline: "auto" }}
      >
        <CardImage src={emptyMigrationReleasesUrl} height={280} />

        <CardContent padding={32}>
          <Box gap={8}>
            <Text variant="h3">Migration release</Text>
            <Box position="relative" top={-2}>
              <Badge title="Beta" size="medium" />
            </Box>
          </Box>

          <Text variant="normal" color="grey11">
            Documents can be added to a migration release via the{" "}
            <Text href={migrationApiDocsUrl} underline color="grey11">
              Migration API (BETA)
            </Text>{" "}
            only. With migration releases, you can preview and publish up to
            1000 documents with one click.
          </Text>

          <Button
            sx={{ marginTop: 16 }}
            onClick={() => setModalOpen(true)}
            size="large"
          >
            Try Migration API
          </Button>
        </CardContent>

        <TryMigrationAPIDialog open={modalOpen} onOpenChange={setModalOpen} />
      </Card>
    )
  }

  return (
    <Card padding={0} sx={{ width: 498, marginTop: 80, marginInline: "auto" }}>
      <CardImage src={emptyMigrationReleasesUrl} height={280} />

      <CardContent padding={32}>
        <Box gap={8}>
          <Text variant="h3">Migration release</Text>
          <Box position="relative" top={-2}>
            <Badge title="Beta" size="medium" />
          </Box>
        </Box>

        <Text variant="normal" color="grey11">
          Migrate content to Prismic using the Migration API. Migrated content
          will show up here, ready to be previewed or published with one click.
        </Text>

        <Button sx={{ marginTop: 16 }} size="large" asChild>
          <a href="https://prismic.io/docs/migration">
            See the Migration API docs
          </a>
        </Button>
      </CardContent>
    </Card>
  )
}

interface TryMigrationAPIDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function TryMigrationAPIDialog(props: TryMigrationAPIDialogProps) {
  const { open, onOpenChange } = props
  const { repository } = staticEditorConfig
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="small">
      <DialogHeader title="Try your first request" icon="migrationRelease" />
      <DialogContent>
        <ScrollArea style={{ padding: 32, gap: 16 }}>
          <Text variant="h4">Try the Migration API now:</Text>
          <Text>
            The Migration API (beta) lets you migrate content to Prismic. With
            the API, you can programmatically create and update documents. While
            the API is in beta, an API key is required.
          </Text>
          <Alert
            subtitle="The following steps will use a demo key with a reduced rate limit to
            access the API. As a result, your queries might return errors during
            peak usage times."
            color="warn"
            noShadow
          />

          <Text variant="h4">Headers</Text>
          <Text>
            You must be an administrator in Prismic to use this API. To
            authenticate your request with the Migration API, include the
            headers <Text color="tomato9">repository</Text> (repository name),{" "}
            <Text color="tomato9">x-api-key</Text> (the demo key) and a{" "}
            <Text color="tomato9">Authorization</Text> (a bearer token).
          </Text>
          <Text>
            For the x-api-key header, insert one of the following demo keys:
          </Text>
          <Suspense fallback={<div>Loading...</div>}>
            <CodeBlock
              language="plaintext"
              code={apiKeyDemo}
              showLineNumbers={false}
            />
          </Suspense>
          <Text>
            Authenticate your request with a bearer token using a{" "}
            <Link href={userSessionTokenDocsUrl}>user session token</Link> or a{" "}
            <Link href={permanentTokenDocsUrl}>permanent token</Link>.
          </Text>
          <CodeBlock
            title="Authentication"
            language="bash"
            code={makeAuthenticationHeadersExample(repository)}
            showLineNumbers={false}
          />

          <Text variant="h4">Create a page</Text>
          <Text>Create a JSON body with the following content:</Text>
          <CodeBlock
            title="Payload"
            language="json"
            code={jsonBodyExample}
            showLineNumbers={false}
          />

          <Text>
            The UID is required only if the type has a UID property. Send the
            POST request to:
          </Text>
          <CodeBlock
            title="Request URL"
            language="plaintext"
            code="https://migration.prismic.io/documents"
            showLineNumbers={false}
          />
          <Text>
            Here is what this looks like as a curl request (please replace the
            example values):
          </Text>
          <CodeBlock
            title="cURL POST request"
            language="bash"
            code={makeCreatePageCurlExample(repository)}
            showLineNumbers={false}
          />

          <Text variant="h4">Learn more</Text>
          <Text>
            Follow the{" "}
            <Link href={migrationApiTryOutUrl}>Migration API Try Out</Link> to
            create real page data in 5 minutes. Read the{" "}
            <Link href={migrationApiDocsUrl}>Migration API documentation</Link>{" "}
            for an in-depth explanation.
          </Text>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

interface LinkProps {
  href: string
  children: string
}
function Link(props: LinkProps) {
  const { href, children } = props
  return (
    <Text href={href} underline color="grey12">
      {children}
    </Text>
  )
}

const apiKeyDemo = `cSaZlfkQlF9C6CEAM2Del6MNX9WonlV86HPbeEJL
pZCexCajUQ4jriYwIGSxA1drZrFxDyFf1S0D1K0P
Yc0mfrkGDw8gaaGKTrzwC3QUZDajv6k73DA99vWN
ySzSEbVMAb5S1oSCQfbVG4mbh9Cb8wlF7BCvKI0L
g2DA3EKWvx8uxVYcNFrmT5nJpon1Vi9V4XcOibJD
CCNIlI0Vz41J66oFwsHUXaZa6NYFIY6z7aDF62Bc`

function makeAuthenticationHeadersExample(repository: string) {
  return `--header 'repository: ${repository}' \\ 
--header 'Authorization: Bearer {{token}}' \\
--header 'x-api-key: {{apiKey}}'`
}

const jsonBodyExample = `{   
  "title": "Example Page Title",  // New document display title   
  "uid": "example-uid",  // New document UID   
  "type": "example-type",  // A type that exists in your repository   
  "lang": "en-us",  // A locale that exists in your repository   
  "data": {} 
}`

function makeCreatePageCurlExample(repository: string) {
  return `curl --location 'https://migration.prismic.io/documents' \\ 
--header 'repository: ${repository}' \\ 
--header 'x-api-key: {{apiKey}}' \\ 
--header 'Content-Type: application/json' \\ 
--header 'Authorization: Bearer {{token}}' \\ 
--data '{
  "title": "Example Page Title",     
  "uid": "example-uid",     
  "type": "example-type",     
  "lang": "en-us",     
  "data": {}  
}'`
}
